import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { IButtonProps } from '@trend-dev/trendtech-ui/components/UI/Button';
import Button from '@trend-dev/trendtech-ui/components/UI/Button';
import { Icon } from '@trend-dev/ui-libs-icons';
import { useBoolean } from '@trend-dev/ui-libs-utils/hooks/useBoolean';

import { useYM } from 'YM/Context';
import { GoalType } from 'YM/declare';

import { camera, grantAccess, microphone } from 'components/Pages/Preparation/const';
import { usePreparationContext } from 'components/Pages/Preparation/Context';

import { safariCameraPermissionManager } from 'manager/safariCameraPermissionManager';

import { EPaths } from 'router/const/paths';

import { useDeviceWidth } from 'hooks/useDeviceWidth';

import { getBrowserName } from 'utils/getBrowserName';
import { getDevicePermission } from 'utils/getDevicePermission';
import { getDevicesPermissionMap } from 'utils/getDevicesPermissionMap';

import { searchParamsNames } from 'constants/searchParamsNames';

import AccessGrantedButton from '../AccessGrantedButton';
import Hint from '../Hint';
import Mark from '../Mark';
import styles from './styles.module.scss';

export function useDeviceList() {
    const [microphonePermissionGranted, { on: setMicrophonePermissionGranted }] = useBoolean(false);
    const [cameraPermissionGranted, { on: setCameraPermissionGranted }] = useBoolean(false);
    const { setIsUserAllowedToEnterTheConference } = usePreparationContext();
    const { isDesktop } = useDeviceWidth();
    const navigate = useNavigate();
    const { triggerYM } = useYM();

    const redirect = useCallback(() => {
        const searchParams = new URLSearchParams();
        const pathname = window.location.pathname.replace(/\/$/, '');

        searchParams.append(searchParamsNames.redirectTo, `${pathname}/${window.location.search}`);
        navigate(`/${EPaths.DEVICE_PERMISSION_DENIED}?${searchParams}`);
    }, []);

    const microphoneButtonClickHandler = useCallback(() => {
        triggerYM({ goal: GoalType.PREPARATION_PAGE_MIC_PERMISSION });
        getDevicePermission({ audio: true })
            .then(() => {
                triggerYM({ goal: GoalType.PREPARATION_PAGE_MIC_PERMISSION_SUCCESS });
                setMicrophonePermissionGranted();
            })
            .catch(redirect);
    }, [redirect]);

    const cameraButtonClickHandler = useCallback(() => {
        triggerYM({ goal: GoalType.PREPARATION_PAGE_CAM_PERMISSION });

        getDevicePermission({ video: true })
            .then(() => {
                const browserName = getBrowserName();
                const isSafari = browserName === 'Safari' || browserName === 'Mobile Safari';

                if (isSafari) safariCameraPermissionManager.setGranted();
                triggerYM({ goal: GoalType.PREPARATION_PAGE_CAM_PERMISSION_SUCCESS });
                setCameraPermissionGranted();
            })
            .catch(redirect);
    }, [redirect]);

    const buttonProps = useMemo<IButtonProps>(() => {
        return {
            borderRadius: 'md',
            kind: 'gray',
            size: isDesktop ? 'sm' : 'md',
        };
    }, [isDesktop]);

    useEffect(() => {
        (async () => {
            const { camera, microphone } = await getDevicesPermissionMap();

            if (camera) setCameraPermissionGranted();
            if (microphone) setMicrophonePermissionGranted();
        })();
    }, []);

    useEffect(() => {
        if (microphonePermissionGranted) {
            setIsUserAllowedToEnterTheConference(true);
        }
    }, [microphonePermissionGranted]);

    return useMemo(() => {
        const microphoneMarkup = {
            content: (
                <div className={styles.content}>
                    {microphone[microphonePermissionGranted ? 'granted' : 'notGranted']}
                    <Hint granted={microphonePermissionGranted} hint={microphone.hint[microphonePermissionGranted ? 'granted' : 'notGranted']} />
                </div>
            ),
            end: microphonePermissionGranted ? (
                <AccessGrantedButton />
            ) : (
                <Button onClick={microphoneButtonClickHandler} {...buttonProps}>
                    {grantAccess}
                </Button>
            ),
        };

        const cameraMarkup = {
            content: <div className={styles.content}>{camera[cameraPermissionGranted ? 'granted' : 'notGranted']}</div>,
            end: cameraPermissionGranted ? (
                <AccessGrantedButton />
            ) : (
                <Button onClick={cameraButtonClickHandler} {...buttonProps}>
                    {grantAccess}
                </Button>
            ),
        };

        return [
            {
                content: (() => {
                    return (
                        <div className={styles.wrapper}>
                            {microphoneMarkup.content}
                            {microphoneMarkup.end}
                        </div>
                    );
                })(),
                icon: microphonePermissionGranted ? (
                    <Mark>
                        <Icon name='Mic_20' />
                    </Mark>
                ) : (
                    <Icon name='MicOff_20' />
                ),
            },
            {
                content: (() => {
                    return (
                        <div className={styles.wrapper}>
                            {cameraMarkup.content}
                            {cameraMarkup.end}
                        </div>
                    );
                })(),

                icon: cameraPermissionGranted ? (
                    <Mark>
                        <Icon name='VideoCamera_20' />
                    </Mark>
                ) : (
                    <Icon name='VideoCameraOff_20' />
                ),
            },
        ];
    }, [microphonePermissionGranted, cameraPermissionGranted, buttonProps]);
}
